import type {
  GTableAlimentBase,
  GTableBoxBackgroundBase,
  GTableBoxPaddingBase,
  GTableHeaderProps,
} from '../types';
import type { Context, ReactNode } from 'react';

import { createContext, useContext, useMemo } from 'react';

export interface GTableProviderProps extends Omit<
  GTableContextType,
  'parsePaddingRow' | 'getHighlightBgColumn'
> {
  children?: ReactNode;
}

export interface GTableContextType {
  columnBody: string;
  columnHead: string;
  listHeader: GTableHeaderProps[];
  alignments: GTableAlimentBase[];
  parsePaddingRow: (index?: number) => GTableBoxPaddingBase;
  getHighlightBgColumn: (index: number) => GTableBoxBackgroundBase;
}

export const GTableContext: Context<GTableContextType | undefined> = createContext<
  GTableContextType | undefined
>(undefined);

export function GTableProvider({
  children,
  columnBody,
  columnHead,
  listHeader,
  alignments,
}: Readonly<GTableProviderProps>): JSX.Element {
  const parsePaddingRow = (index?: number) => {
    const padding = index !== undefined ? listHeader[index].padding : undefined;
    const defaultPaddingTable: GTableBoxPaddingBase = {
      padding: '200',
    };
    const paddingBase = padding ? { ...defaultPaddingTable, ...padding } : defaultPaddingTable;

    const isFirst = index === 0;
    const isLast = index === listHeader.length - 1;

    const paddingBox = {
      ...paddingBase,
      paddingInlineStart:
        isFirst && !padding?.paddingInlineStart ? '0' : paddingBase.paddingInlineStart,
      paddingInlineEnd: isLast && !padding?.paddingInlineEnd ? '0' : paddingBase.paddingInlineEnd,
    };
    return paddingBox;
  };
  const getHighlightBgColumn = (index: number): GTableBoxBackgroundBase => {
    return listHeader[index].highlight ? { background: 'bg-surface-info' } : {};
  };

  const contextValue = useMemo(
    () => ({
      columnBody,
      columnHead,
      alignments,
      listHeader,
      parsePaddingRow,
      getHighlightBgColumn,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnBody, columnHead, alignments, listHeader],
  );

  return <GTableContext.Provider value={contextValue}>{children}</GTableContext.Provider>;
}

export const useGTableContext = () => {
  const context = useContext(GTableContext);
  if (!context) {
    throw new Error('GEMX_CONTEXT_IS_NOT_DEFINED');
  }
  return context;
};
